import { useCallback, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Card, CardContent, CardHeader, Grid, Theme } from '@mui/material';
import { useApplicationsStore } from '@lib';
import { ApplicationSearchParams } from '@shared/api';
import { PermissionsUtils, RoutePaths, useRouteHook } from '@shared';
import { PermissionLevel, PermissionType } from '@shared/types';
import { ApplicationSearchForm, ApplicationsTable } from './widgets';
import { DelayedLinearProgress } from '@widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  headerContainer: {
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    textAlign: 'center',
  },
  button: {
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

export const ApplicationsPage = () => {
  const { loading, applications, getApplications } = useApplicationsStore();

  const [filterCriteria, setFilterCriteria] = useState<ApplicationSearchParams>({});

  const classes = useStyles();
  const { navigate } = useRouteHook();

  const canCreate = PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.WRITE);

  const onSubmit = useCallback(async (searchParams: ApplicationSearchParams) => getApplications(searchParams), []);

  const onFilterChange = (name: string, value: Date | string | null) => setFilterCriteria((prev) => ({ ...prev, [name]: value }));

  return (
    <Grid container alignContent='center'>
      <Grid item xs={12} md={3} xl={2}>
        <Card>
          <CardHeader title='Filter Applications' />
          <CardContent style={{ paddingTop: 0 }}>
            <ApplicationSearchForm filterCriteria={filterCriteria} onFilterChange={onFilterChange} onSubmit={onSubmit} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={9} xl={10}>
        <Card>
          <DelayedLinearProgress loading={loading} />
          <div className={classes.headerContainer}>
            <CardHeader title='Applications' />
            {canCreate && (
              <Button
                variant='contained'
                color='primary'
                className={classes.button}
                onClick={() => navigate(RoutePaths.applicationsNew.onNavigate())}
              >
                Create New Application
              </Button>
            )}
          </div>
          {applications && <ApplicationsTable loading={loading} data={applications} />}
        </Card>
      </Grid>
    </Grid>
  );
};
